/**
 * The Proposals table's view and its open side panel, in the URL (bata-ui issue 342 §3, PR B,
 * `docs/adr/6020`; CLAUDE.md §3.4 — the URL is the state).
 *
 *   `?proposalsView=store`     one row per store; its side panel lists the store's articles
 *   `?proposalsView=article`   one row per article; its side panel lists the article's stores
 *   no key                     one row per store × article × size line, as the page always drew
 *   `?proposalsPanel=<key>`    the store (or article) whose panel is open — only with a view
 *
 * **Not `?view=` and not `?by=`.** `?view=explain` on this address is the old explain link, sent
 * to «Perché» (`AllocationScreen`), and the shell carries every query key from one page to the
 * next (`rail-layout.ts`), so a bare `?by=store` would follow the planner to network health and
 * regroup its table. The two keys are this page's own.
 *
 * The line view is the one with no key: a link shared before the views existed opens what it
 * always opened. Pure parses and writes over a search string (rule 4): a test asserts on a string.
 */

import { ALLOCATION_WHY_PATH } from './allocation-paths';

export const PROPOSALS_VIEW_PARAM = 'proposalsView';
export const PROPOSALS_PANEL_PARAM = 'proposalsPanel';

/** The three views, in the order the switch offers them. */
export const PROPOSALS_VIEWS = ['store', 'article', 'line'] as const;
export type ProposalsView = (typeof PROPOSALS_VIEWS)[number];

export interface ProposalsViewState {
  readonly view: ProposalsView;
  /** The store or article whose side panel is open; `null` in the line view or with none open. */
  readonly panel: string | null;
}

/** The view and the open panel `search` names. An unknown view is the line view. Pure. */
export function readProposalsView(search: string): ProposalsViewState {
  const params = new URLSearchParams(search);
  const raw = params.get(PROPOSALS_VIEW_PARAM);
  const view: ProposalsView = raw === 'store' || raw === 'article' ? raw : 'line';
  const panel = params.get(PROPOSALS_PANEL_PARAM);
  return { view, panel: view === 'line' || panel === null || panel.trim() === '' ? null : panel };
}

/**
 * `search` with the view and panel written, every other key kept — the run, the ranking and the
 * filters stay as they are. Changing the view closes the panel: a store key is not an article. Pure.
 */
export function writeProposalsView(search: string, next: ProposalsViewState): string {
  const params = new URLSearchParams(search);
  if (next.view === 'line') params.delete(PROPOSALS_VIEW_PARAM);
  else params.set(PROPOSALS_VIEW_PARAM, next.view);
  if (next.view === 'line' || next.panel === null) params.delete(PROPOSALS_PANEL_PARAM);
  else params.set(PROPOSALS_PANEL_PARAM, next.panel);
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/**
 * The «Perché» page on one store × article — `/inseason/allocation-why?row=<store>|<article>` —
 * with the page's run, ranking and filters carried, as the rail carries them; this page's own two
 * keys are left behind, since «Perché» has no views. Pure.
 */
export function whyRowHref(search: string, rowId: string): string {
  const params = new URLSearchParams(search);
  params.delete(PROPOSALS_VIEW_PARAM);
  params.delete(PROPOSALS_PANEL_PARAM);
  params.set('row', rowId);
  return `${ALLOCATION_WHY_PATH}?${params.toString()}`;
}
